<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import type { ToggleValue, ToggleOption } from './WheelsToggle.types'

const props = withDefaults(defineProps<{
  value: ToggleValue
  options: ToggleOption[]
  disabled?: boolean
  /** Make each segment same width & fill parent */
  block?: boolean
  /** Minimum width per segment (e.g., 120) */
  minItemWidth?: number | null
  ariaLabel?: string
  /** Set the color of the active selected button */
  color?: string
}>(), {
  disabled: false,
  block: false,
  minItemWidth: 120,
  ariaLabel: 'Toggle',
  color: 'cu-blue', // default accent
})

const emit = defineEmits<{
  (e: 'input', value: ToggleValue): void
  (e: 'change', value: ToggleValue): void
}>()

const btnRefs = ref<HTMLElement[]>([])

const activeIndex = computed(() => {
  const i = props.options.findIndex(o => o.value === props.value)
  return i === -1 ? 0 : i
})

function focusIndex(i: number) {
  nextTick(() => {
    const el = btnRefs.value[i]
    if (el) el.focus()
  })
}

function selectIndex(i: number) {
  const opt = props.options[i]
  if (!opt || props.disabled || opt.disabled) return
  emit('input', opt.value)
  emit('change', opt.value)
}

function nextEnabled(from: number, dir: 1 | -1) {
  const total = props.options.length
  let i = from
  for (let j = 0; j < total; j++) {
    i = (i + dir + total) % total
    const opt = props.options[i]
    if (opt && !opt.disabled) return i
  }
  return from
}

function onKeydown(i: number, e: KeyboardEvent) {
  if (props.disabled) return
  switch (e.key) {
    case 'ArrowRight':
      e.preventDefault()
      focusIndex(nextEnabled(i, 1))
      break
    case 'ArrowLeft':
      e.preventDefault()
      focusIndex(nextEnabled(i, -1))
      break
    case 'Enter':
    case ' ':
      e.preventDefault()
      selectIndex(i)
      break
  }
}

onMounted(() => {
  focusIndex(activeIndex.value)
})
</script>

<template>
  <div
    class="wheels-toggle"
    :class="{
      'wheels-toggle--block': block,
      'wheels-toggle--disabled': disabled,
    }"
    role="radiogroup"
    :aria-label="ariaLabel"
    :style="{
      '--wt-active-bg': `var(--${color}-500)`,
    }"
  >
    <button
      v-for="(opt, i) in options"
      :key="String(opt.value)"
      class="wheels-toggle__btn"
      :class="{
        'wheels-toggle__btn--active': i === activeIndex,
        'wheels-toggle__btn--block': block,
        'wheels-toggle__btn--disabled': disabled || opt.disabled
      }"
      ref="btnRefs"
      role="radio"
      :aria-checked="i === activeIndex"
      :tabindex="i === activeIndex ? 0 : -1"
      :disabled="disabled || opt.disabled"
      :data-active="i === activeIndex"
      :style="{ minWidth: block && minItemWidth ? minItemWidth + 'px' : undefined }"
      @click="selectIndex(i)"
      @keydown="onKeydown(i, $event)"
    >
      <span class="wheels-toggle__label">{{ opt.label }}</span>
    </button>
  </div>
</template>

<style scoped lang="scss">
.wheels-toggle {
  --wt-bg: var(--cu-gray-50);
  --wt-fg: var(--cu-gray-400);
  --wt-active-bg: var(--cu-blue-500);
  --wt-active-fg: #ffffff;
  --wt-disabled-opacity: 0.5;
  --wt-gap: 4px;
  --wt-pad-v: 6px;
  --wt-pad-h: 8px;

  display: inline-flex;
  align-items: center;
  gap: var(--wt-gap);
  background: var(--wt-bg);
  border-radius: 999px;
  padding: var(--wt-pad-v) var(--wt-pad-h);
  user-select: none;
}

.wheels-toggle--block {
  width: 100%;
}

.wheels-toggle--disabled {
  opacity: var(--wt-disabled-opacity);
  pointer-events: none;
}

.wheels-toggle__btn {
  display: inline-flex; 
  align-items: center; 
  justify-content: center; 
  gap: 8px; 
  height: 36px; 
  padding: 4px 12px; 
  border-radius: 20px;
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--wt-fg);
  font-size: 1rem;
  font-weight: 400;
  letter-spacing: 0.15px;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;

  &:hover,
  &:focus {
    background: transparent;
    box-shadow: none;
  }

  &--active {
    background: var(--wt-active-bg);
    color: var(--wt-active-fg);
    font-weight: 600;

    &:hover,
    &:focus {
      background: var(--wt-active-bg);
      box-shadow: none;
    }
  }

  &--block {
    flex: 1 1 0;
  }

  &--disabled {
    opacity: var(--wt-disabled-opacity);
    pointer-events: none;
  }

  &:focus-visible {
    outline: 2px solid rgba(25, 118, 210, 0.45);
    outline-offset: 2px;
  }
}

.wheels-toggle__label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
